import React, { useEffect, useMemo, useState } from "react";
import YhModal from "../../../../ui/YhModal";
import YhSelect from "../../../../ui/YhSelect";
import YhMultiSelect from "../../../../ui/YhMultiSelect";
import YhButton from "../../../../ui/YhButton";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../../../helpers/methods";
import { assignLeadsToUser } from "../../../BulkAssign/components/ShowAll/ShowAllService";
import { fetchAutomationRoutingOptions } from "../../../../services/organizationService";
import { loadTenantOrganizationsOnce } from "../../../../Shared/Organization/orgNameCache";
import { store } from "../../../../utils/store";
import { canAssignToSelf as canAssignToSelfFn } from "../../../../helpers/salesLeadHelpers";
import { loadViewerTeamIds, loadViewerAssignScope, ViewerAssignScope } from "../../../../helpers/assignmentScope";

type Props = {
  open: boolean;
  onClose: () => void;
  onAssigned?: () => void;
  leadIds: string[];
  sourceHint?: string;
  /** Active org from header switcher */
  defaultOrganizationId?: string;
  /** Org ids inferred from selected lead rows (optional) */
  leadOrganizationIds?: string[];
};

type OrgOpt = { label: string; value: string };
type TeamOpt = {
  label: string;
  value: string;
  departmentId?: string;
};
type DeptOpt = { label: string; value: string };
type MemberOpt = {
  label: string;
  value: string;
  teamIds: string[];
  isAdmin?: boolean;
};

const isAdminMember = (m: any) =>
  !!m?.isAdmin ||
  !!m?.isSuperAdmin ||
  ["org_admin", "sub_admin", "super_admin", "admin"].includes(
    String(m?.roleSlug || "").toLowerCase()
  );

const BulkAssignModal = ({
  open,
  onClose,
  onAssigned,
  leadIds,
  sourceHint,
  defaultOrganizationId = "",
  leadOrganizationIds = [],
}: Props) => {
  const [orgOptions, setOrgOptions] = useState<OrgOpt[]>([]);
  const [organizationId, setOrganizationId] = useState("");
  const [teamOptions, setTeamOptions] = useState<TeamOpt[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<DeptOpt[]>([]);
  const [departmentIds, setDepartmentIds] = useState<string[]>([]);
  const [teamScoped, setTeamScoped] = useState(false);
  const [assignScope, setAssignScope] = useState<ViewerAssignScope | null>(null);
  const [allMembers, setAllMembers] = useState<MemberOpt[]>([]);
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState("");

  const auth = store.getState().auth || {};
  const canAssignToSelf = canAssignToSelfFn(auth);
  const myUserId = String(auth.userId || "").trim();

  // Pick default org: single lead-org → active org → first available
  const resolveDefaultOrg = (orgs: OrgOpt[]) => {
    const uniqueLeadOrgs = Array.from(
      new Set(
        (leadOrganizationIds || [])
          .map(String)
          .filter((id) => id && id !== "all")
      )
    );
    if (uniqueLeadOrgs.length === 1) return uniqueLeadOrgs[0];
    if (
      defaultOrganizationId &&
      defaultOrganizationId !== "all" &&
      orgs.some((o) => o.value === defaultOrganizationId)
    ) {
      return defaultOrganizationId;
    }
    if (uniqueLeadOrgs.length && orgs.some((o) => o.value === uniqueLeadOrgs[0])) {
      return uniqueLeadOrgs[0];
    }
    return orgs[0]?.value || "";
  };

  useEffect(() => {
    if (!open) return;
    setTeamIds([]);
    setMemberIds([]);
    setDepartmentIds([]);
    setTeamOptions([]);
    setAllMembers([]);
    setProgress("");
    (async () => {
      setLoadingOrgs(true);
      try {
        const res = await loadTenantOrganizationsOnce();
        const list = Array.isArray(res?.data) ? res.data : [];
        const opts = list
          .map((o: any) => ({
            label: String(
              o.brandName || o.name || o.legalName || o.slug || o._id
            ),
            value: String(o._id),
          }))
          .filter((o: OrgOpt) => o.value);
        setOrgOptions(opts);
        setOrganizationId(resolveDefaultOrg(opts));
      } catch (e: any) {
        openErrorNotification(e?.message || "Failed to load organizations");
      } finally {
        setLoadingOrgs(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !organizationId || organizationId === "all") {
      setTeamOptions([]);
      setAllMembers([]);
      setTeamIds([]);
      setMemberIds([]);
      return;
    }
    (async () => {
      setLoadingTeams(true);
      setTeamIds([]);
      setMemberIds([]);
      try {
        const [res, viewerTeamIds, scope] = await Promise.all([
          fetchAutomationRoutingOptions(organizationId),
          loadViewerTeamIds(),
          loadViewerAssignScope(),
        ]);
        const data = res?.data || res || {};
        setAssignScope(scope);
        const allow =
          scope.role === "org"
            ? viewerTeamIds == null
              ? null
              : new Set(viewerTeamIds.map(String))
            : new Set(scope.teamIds.map(String));
        setTeamScoped(scope.role !== "org");
        const teams = (Array.isArray(data.teams) ? data.teams : []).filter(
          (t: any) => allow == null || allow.has(String(t.teamId || t._id))
        );
        const members = (Array.isArray(data.members) ? data.members : []).filter(
          (m: any) => {
            if (scope.role === "member") return String(m.userId || m._id) === scope.userId;
            if (allow == null) return true;
            return (m.teamIds || []).some((tid: string) => allow.has(String(tid)));
          }
        );
        const deptIds = new Set(
          teams.map((t: any) => String(t.departmentId || "")).filter(Boolean)
        );
        setDepartmentOptions(
          (Array.isArray(data.departments) ? data.departments : [])
            .filter((d: any) => allow == null || deptIds.has(String(d._id)))
            .map((d: any) => ({
              label: String(d.name || d._id),
              value: String(d._id),
            }))
        );
        setTeamOptions(
          teams
            .map((t: any) => ({
              label: t.departmentName
                ? `${t.teamName || t.name} (${t.departmentName})`
                : String(t.teamName || t.name || t.teamId || t._id),
              value: String(t.teamId || t._id || ""),
              departmentId: t.departmentId ? String(t.departmentId) : "",
            }))
            .filter((t: TeamOpt) => t.value)
        );
        let mapped: MemberOpt[] = members
          .map((m: any) => ({
            label: String(
              m.name || m.user_name || m.email || m.userId
            ) + (isAdminMember(m) ? " (Admin)" : ""),
            value: String(m.userId || m._id || ""),
            teamIds: Array.isArray(m.teamIds)
              ? m.teamIds.map(String)
              : m.teamId
                ? [String(m.teamId)]
                : [],
            isAdmin: isAdminMember(m),
          }))
          .filter((m: MemberOpt) => m.value);

        // Ensure signed-in admin appears even if not in org membership list
        if (canAssignToSelf && myUserId) {
          if (!mapped.some((m) => m.value === myUserId)) {
            mapped = [
              {
                label: auth.userName
                  ? `${auth.userName} (Me)`
                  : "Assign to me",
                value: myUserId,
                teamIds: [],
                isAdmin: true,
              },
              ...mapped,
            ];
          }
        }
        setAllMembers(mapped);
        if (scope.role === "leader" || scope.role === "member") {
          const lockedTeams = scope.teamIds.filter((id) =>
            teams.some((t: any) => String(t.teamId || t._id) === String(id))
          );
          setTeamIds(lockedTeams.length ? lockedTeams : scope.teamIds);
          if (scope.role === "member" && scope.userId) {
            setMemberIds([scope.userId]);
          }
        }
      } catch (e: any) {
        openErrorNotification(
          e?.message || "Failed to load teams for this organization"
        );
        setTeamOptions([]);
        setAllMembers([]);
      } finally {
        setLoadingTeams(false);
      }
    })();
  }, [open, organizationId]);

  const visibleTeamOptions = useMemo(
    () =>
      departmentIds.length
        ? teamOptions.filter((t) =>
            departmentIds.includes(String(t.departmentId || ""))
          )
        : teamOptions,
    [teamOptions, departmentIds]
  );

  const adminMembers = useMemo(
    () => allMembers.filter((m) => m.isAdmin),
    [allMembers]
  );

  const memberOptions = useMemo(() => {
    if (assignScope?.role === "member") {
      const self = allMembers.find((m) => m.value === assignScope.userId);
      return [
        self || {
          label: assignScope.userName || "You",
          value: assignScope.userId,
          teamIds: assignScope.teamIds,
        },
      ];
    }
    // Admins always visible (even with no team selected) for org-wide assigners
    const byId = new Map<string, MemberOpt>();
    if (!assignScope || assignScope.role === "org") {
      adminMembers.forEach((m) => byId.set(m.value, m));
    }

    const scopeIds = teamIds.length
      ? teamIds
      : departmentIds.length
        ? visibleTeamOptions.map((t) => t.value)
        : teamScoped
          ? visibleTeamOptions.map((t) => t.value)
          : [];
    if (scopeIds.length) {
      allMembers
        .filter((m) =>
          (m.teamIds || []).some((tid) => scopeIds.includes(tid))
        )
        .forEach((m) => {
          if (!byId.has(m.value)) byId.set(m.value, m);
        });
    }
    return Array.from(byId.values());
  }, [allMembers, teamIds, departmentIds, visibleTeamOptions, teamScoped, adminMembers, assignScope]);

  // Drop members that no longer match selected teams (keep admins)
  useEffect(() => {
    const allowed = new Set(memberOptions.map((m) => m.value));
    setMemberIds((prev) => prev.filter((id) => allowed.has(id)));
  }, [memberOptions]);

  const runAssign = async (userIds: string[], teams: string[]) => {
    if (!leadIds.length) {
      openErrorNotification("Select at least one lead");
      return;
    }
    if (!organizationId || organizationId === "all") {
      openErrorNotification("Select an organization");
      return;
    }
    if (!userIds.length) {
      openErrorNotification("Select at least one member to re-assign");
      return;
    }

    setSaving(true);
    setProgress(`Re-assigning 0 / ${leadIds.length} lead(s)…`);

    try {
      const payload: any = {
        lead_id: leadIds.map((id) => `'${id}'`).join(","),
        user_id: userIds.map((id) => `'${id}'`).join(","),
      };
      if (teams.length) {
        payload.team_id = teams.map((id) => `'${id}'`).join(",");
      }

      const onProgress = (done: number, total: number) => {
        setProgress(`Re-assigning ${done} / ${total} lead(s)…`);
      };

      const res = await assignLeadsToUser(payload, onProgress);
      if (!(res?.success === 1 || res?.success === true)) {
        openErrorNotification(res?.message || "Re-assign failed");
        return;
      }
      openSuccessNotification(
        res?.message || `Re-assigned ${leadIds.length} lead(s) successfully`
      );
      onAssigned?.();
      onClose();
    } catch (e: any) {
      openErrorNotification(e?.message || "Unable to re-assign leads");
    } finally {
      setSaving(false);
      setProgress("");
    }
  };

  const onSave = async () => {
    if (!teamIds.length && !memberIds.some((id) =>
      adminMembers.some((a) => a.value === id)
    )) {
      openErrorNotification(
        "Select at least one team, or assign to an Admin without a team"
      );
      return;
    }
    await runAssign(memberIds, teamIds);
  };

  const onAssignToMe = async () => {
    if (!canAssignToSelf || !myUserId) {
      openErrorNotification(
        "Only Admin, Super Admin, or Team Lead can use Assign to me"
      );
      return;
    }
    setMemberIds([myUserId]);
    await runAssign([myUserId], teamIds);
  };

  const busy = loadingOrgs || loadingTeams || saving;
  const lockTeam =
    assignScope?.role === "leader" || assignScope?.role === "member";
  const lockMember = assignScope?.role === "member";

  return (
    <YhModal
      open={open}
      onClose={onClose}
      title="Re-assign leads"
      width={520}
    >
      <div className="flex flex-col gap-4 p-1">
        <p className="text-sm text-[#6B7280]">
          Re-assign{" "}
          <span className="font-semibold text-[#111827]">{leadIds.length}</span>{" "}
          selected lead{leadIds.length === 1 ? "" : "s"}
          {sourceHint ? (
            <>
              {" "}
              · Source:{" "}
              <span className="font-semibold text-[#111827]">{sourceHint}</span>
            </>
          ) : null}
          . This replaces the current member assignment for all selected leads.
          Teams are optional for Admin assignees.
        </p>

        <YhSelect
          label="Organization"
          searchable
          searchPlaceholder="Search organizations..."
          disabled={busy}
          value={organizationId}
          onChange={(e: any) => setOrganizationId(String(e.target.value || ""))}
          options={[
            { label: loadingOrgs ? "Loading…" : "Select organization", value: "" },
            ...orgOptions,
          ]}
        />

        <YhMultiSelect
          label="Department"
          placeholder={
            organizationId
              ? loadingTeams
                ? "Loading departments…"
                : departmentOptions.length
                  ? "Select departments..."
                  : "No departments for your teams"
              : "Select organization first"
          }
          searchPlaceholder="Search departments..."
          showOptionIcon={false}
          showSelectAll
          maxVisibleChips={3}
          clearable={!lockTeam}
          disabled={busy || lockTeam || !organizationId || loadingTeams}
          options={departmentOptions}
          value={departmentIds}
          onChange={(vals) => {
            const next = (vals || []).map(String).filter(Boolean);
            setDepartmentIds(next);
            setTeamIds((prev) =>
              prev.filter((id) =>
                teamOptions.some(
                  (t) =>
                    t.value === id &&
                    (!next.length ||
                      next.includes(String(t.departmentId || "")))
                )
              )
            );
          }}
        />

        <YhMultiSelect
          label="Assigned To (teams)"
          placeholder={
            organizationId
              ? loadingTeams
                ? "Loading teams…"
                : teamOptions.length
                  ? "Select teams..."
                  : "No teams found for this org"
              : "Select organization first"
          }
          searchPlaceholder="Search teams..."
          showOptionIcon={false}
          showSelectAll
          maxVisibleChips={3}
          clearable={!lockTeam}
          disabled={busy || lockTeam || !organizationId || loadingTeams}
          options={
            lockTeam
              ? (assignScope?.teams || []).map((team) => ({
                  label:
                    visibleTeamOptions.find((option) => option.value === team.id)
                      ?.label || team.label,
                  value: team.id,
                }))
              : visibleTeamOptions
          }
          value={teamIds}
          onChange={(vals) =>
            setTeamIds((vals || []).map(String).filter(Boolean))
          }
        />

        <YhMultiSelect
          label="Assigned To Member"
          placeholder={
            !organizationId
              ? "Select organization first"
              : memberOptions.length
                ? "Select members..."
                : teamIds.length
                  ? "No members on selected teams"
                  : "Select teams, or pick an Admin"
          }
          searchPlaceholder="Search members..."
          showOptionIcon={false}
          showSelectAll
          maxVisibleChips={3}
          clearable
          disabled={
            busy || lockMember || !organizationId || loadingTeams || !memberOptions.length
          }
          options={memberOptions}
          value={memberIds}
          onChange={(vals) =>
            setMemberIds((vals || []).map(String).filter(Boolean))
          }
        />

        {progress ? (
          <p className="text-xs font-medium text-[#A02448]">{progress}</p>
        ) : null}

        <div className="mt-1 flex flex-wrap justify-end gap-2">
          <YhButton variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </YhButton>
          {canAssignToSelf ? (
            <YhButton
              variant="secondary"
              onClick={onAssignToMe}
              disabled={busy || !organizationId}
            >
              Assign to me
            </YhButton>
          ) : null}
          <YhButton onClick={onSave} disabled={busy || lockMember || !memberIds.length}>
            {saving ? "Re-assigning…" : "Re-assign"}
          </YhButton>
        </div>
      </div>
    </YhModal>
  );
};

export default BulkAssignModal;
